import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import Card from './ui/Card';
import Button from './ui/Button';
import PasswordInput from './PasswordInput';

const DeleteAccount: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDeleteClick = () => {
    setShowConfirmation(true);
    setError(null);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const handleDeleteConfirm = async () => {
    // Vérifier que le mot de passe est fourni
    if (!password) {
      setError('Veuillez entrer votre mot de passe pour confirmer la suppression');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      
      const result = await authService.deleteAccount(password);
      
      if (result.success) {
        // Déconnecter l'utilisateur et rediriger vers la page d'accueil
        if (logout) {
          logout();
        }
        navigate('/');
      } else {
        setError(result.error || 'Une erreur est survenue lors de la suppression de votre compte.');
      }
    } catch (err: any) {
      console.error('Erreur lors de la suppression du compte:', err);
      setError(err.response?.data?.error || 'Une erreur est survenue lors de la suppression de votre compte.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAbort = () => {
    setShowConfirmation(false);
    setPassword('');
  };

  return (
    <Card title="Supprimer mon compte" className="border-red-300">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      {!showConfirmation ? (
        <div>
          <p className="text-gray-600 mb-4">
            La suppression de votre compte est définitive et entraînera la perte de toutes vos données, y compris vos clients, statistiques et historique d'utilisation.
          </p>
          <Button 
            variant="danger" 
            onClick={handleDeleteClick}
            disabled={isSubmitting}
          >
            Supprimer mon compte
          </Button>
        </div>
      ) : (
        <div className="bg-red-50 border border-red-200 p-4 rounded">
          <h3 className="text-lg font-medium text-red-900 mb-2">Êtes-vous absolument sûr de vouloir supprimer votre compte ?</h3>
          <p className="text-red-700 mb-4">
            Cette action est <strong>irréversible</strong>. Toutes vos données seront définitivement supprimées de nos serveurs conformément au RGPD.
            Si vous avez un abonnement actif, celui-ci sera également annulé.
          </p>
          
          <div className="mb-4">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Veuillez entrer votre mot de passe pour confirmer
            </label>
            <PasswordInput
              value={password}
              onChange={handlePasswordChange}
              label=""
              placeholder="Entrez votre mot de passe"
              required={true}
              className="w-full"
            />
          </div>
          
          <div className="flex space-x-3">
            <Button 
              variant="danger" 
              onClick={handleDeleteConfirm}
              disabled={isSubmitting || !password}
            >
              {isSubmitting ? 'Suppression en cours...' : 'Confirmer la suppression'}
            </Button>
            <Button 
              variant="secondary" 
              onClick={handleDeleteAbort}
              disabled={isSubmitting}
            >
              Annuler
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};

export default DeleteAccount;
